import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { UserService } from 'src/app/shared/service/user.service';
import { User } from 'src/app/shared/models/user.model';
import { DateModel } from 'src/app/shared/models/date.model';
import { City } from 'src/app/shared/models/city';
import { Province } from 'src/app/shared/models/province';
import { SubSink } from 'subsink';
import { ActivatedRoute, Router } from '@angular/router';
import { DateService } from 'src/app/shared/service/date.service';
import Swal from 'sweetalert2';
import { HttpError } from 'src/app/shared/models/http-error.model';
import { Card } from 'src/app/shared/models/card.model';
import { Iban } from 'src/app/shared/models/iban.model';
import { ServerDataSource } from 'ng2-smart-table';
import { HttpClient } from '@angular/common/http';
import { SmartTableService } from 'src/app/shared/service/smart-table.service';
import { DateFormatPipe } from 'src/app/shared/pipes/jdate.pipe';
import { DecimalPipe } from '@angular/common';
import { AuthenticationService } from 'src/app/shared/service/authentication.service';
import { FeeService } from 'src/app/shared/service/fee.service';
import { UserAssetModel } from 'src/app/shared/models/user-asset.model';
import { NbDialogService, NbTabComponent } from '@nebular/theme';
import { environment } from 'src/environments/environment';
import Viewer from 'viewerjs';
import { NoteComponent } from '../../../shared/components/note/note.component';
import { Label } from '../../../shared/models/label.model';
import { combineLatest, Observable } from 'rxjs';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  ValidationErrors,
  Validators
} from '@angular/forms';
import { UserLimit } from '../../../shared/models/user-limit.model';
import { Currency } from '../../../shared/models/currency.model';
import Big from 'big.js';
import { ImpersonateTutorialComponent } from './components/impersonate-tutorial/impersonate-tutorial.component';

@Component({
  selector: 'app-user-edit',
  templateUrl: './user-edit.component.html',
  styleUrls: ['./user-edit.component.scss']
})
export class UserEditComponent implements OnInit, OnDestroy {
  Toast = Swal.mixin({
    toast: true,
    position: 'top',
    showConfirmButton: false,
    timer: 3000,
    timerProgressBar: true,
    didOpen: (toast) => {
      toast.addEventListener('mouseenter', Swal.stopTimer);
      toast.addEventListener('mouseleave', Swal.resumeTimer);
    }
  });
  sub = new SubSink();
  user = new User();
  limit = new UserLimit();
  isSettingLimit = false;
  birthday = {} as DateModel;
  user2FA = false;
  provinces: Province[] = [];
  provinceLoading = true;
  provinceCity: City[] = [];
  cityLoading = false;
  noLimit = '';
  error: any = {};
  userId = 0;
  userInfoLoading = false;
  userVerifiedLoading = false;
  ibanLoading = false;
  creditCardLoading = false;
  creditCards: Card[] = [];
  userIban: Iban[] = [];
  selectedCard!: Card;
  selectedIban!: Iban;
  deleteLoading = false;
  tableLoading = true;
  feeLoading = false;
  filterLoading = false;
  getTokenLoading = false;
  userAsset!: UserAssetModel;
  tab!: NbTabComponent;
  isDefault = false;
  contradictionLoading = false;
  viewers: Viewer[] = [];
  labels!: Observable<Label[]>;
  userLabels: Label[] = [];
  userWithdrawLimits: UserLimit[] = [];
  currencies: Currency[] = [];
  changePassword = new FormGroup(
    {
      password: new FormControl('', [
        Validators.required,
        Validators.minLength(8),
        Validators.maxLength(255)
      ]),
      rePassword: new FormControl('', [Validators.required])
    },
    { validators: this.passwordMatch('password', 'rePassword') }
  );

  password = this.changePassword.get('password');

  constructor(
    private userService: UserService,
    private http: HttpClient,
    private dateService: DateService,
    private feeService: FeeService,
    private decimalPipe: DecimalPipe,
    private smartTableService: SmartTableService,
    private dateFormatPipe: DateFormatPipe,
    private activatedRoute: ActivatedRoute,
    private authenticationService: AuthenticationService,
    private dialogService: NbDialogService,
    private router: Router
  ) {

  }

  getUserLabels(): void {
    this.sub.sink = this.userService.getUserLabel(this.userId).subscribe((labels) => {
      this.userLabels = labels;
    });
  }

  changeUserPassword(): void {
    this.userInfoLoading = true;
    this.sub.sink = this.userService.updatePassword(this.userId, this.password?.value!).subscribe(
      () => {
        this.Toast.fire({
          icon: 'success',
          title: 'رمز عبور با موفقیت تغییر یافت'
        });
        this.userInfoLoading = false;
      },
      (response: HttpError) => {
        if (response.status !== 422) {
          this.Toast.fire({ icon: 'error', title: response.error });
        } else {
          this.error = response.validationError;
        }
        this.userInfoLoading = false;
      }
    );
  }

  passwordMatch(password: string, confirmPassword: string) {
    return (formGroup: AbstractControl): ValidationErrors | null => {
      const passwordControl = formGroup.get(password);
      const confirmPasswordControl = formGroup.get(confirmPassword);

      if (!passwordControl || !confirmPasswordControl) {
        return null;
      }

      if (confirmPasswordControl.errors && !confirmPasswordControl.errors.passwordMismatch) {
        return null;
      }

      if (passwordControl.value !== confirmPasswordControl.value) {
        confirmPasswordControl.setErrors({ passwordMismatch: true });
        return { passwordMismatch: true };
      } else {
        confirmPasswordControl.setErrors(null);
        return null;
      }
    };
  }

  initImageViewer(id: string): void {
    const element = document.getElementById(id);
    if (element) {
      const viewer = new Viewer(element, {
        inline: false,
        zoomRatio: 0.3,
        toolbar: {
          zoomIn: true,
          zoomOut: true,
          oneToOne: true,
          reset: false,
          prev: false,
          play: false,
          next: false,
          rotateLeft: true,
          rotateRight: true,
          flipHorizontal: true,
          flipVertical: true
        }
      });
      this.viewers.push(viewer);
    }
  }

  private getUser(): void {
    this.userService.getUserInfo(this.userId).subscribe((user) => {
      this.user = user;
      if (this.user.feePercent === this.user.defaultTransactionFee) {
        this.isDefault = true;
      }
      this.birthday = this.dateService.convertStringDateToDate(user.birthday);
      this.getProvinceCities(this.user.provinceId);
    });
  }

  setBirthday(date: DateModel): void {
    this.user.birthday = this.dateService.convertDateToString(date);
  }

  getProvinceCities(id: number): void {
    this.cityLoading = true;
    this.sub.sink = this.userService.getProvinceCity(id).subscribe(
      (cities) => {
        this.provinceCity = cities;
        this.cityLoading = false;
      },
      () => (this.cityLoading = false)
    );
  }

  updateUserInfo(): void {
    this.userInfoLoading = true;
    this.error = {};
    const user: any = {};
    if (this.user.firstName !== null) {
      user.firstName = this.user.firstName === '' ? null : this.user.firstName;
    }
    if (this.user.lastName !== null) {
      user.lastName = this.user.lastName === '' ? null : this.user.lastName;
    }
    if (this.user.provinceId !== null) {
      user.provinceId = this.user.provinceId;
    }
    if (this.user.cityId !== null) {
      user.cityId = this.user.cityId;
    }
    if (this.user.birthday !== null) {
      user.birthday = this.user.birthday === '' ? null : this.user.birthday;
    }
    if (this.user.address !== null) {
      user.address = this.user.address === '' ? null : this.user.address;
    }
    if (this.user.email !== null) {
      user.email = this.user.email === '' ? null : this.user.email;
    }
    if (this.user.phone !== null) {
      user.phone = this.user.phone === '' ? null : this.user.phone;
    }
    if (this.user.gender !== null) {
      user.gender = this.user.gender;
    }
    if (this.user.nationalId !== null) {
      user.nationalId = this.user.nationalId;
    }
    if (this.user.foreignerId !== null) {
      user.foreignerId = this.user.foreignerId;
    }
    if (this.user.postalCode !== null) {
      user.postalCode = this.user.postalCode === '' ? null : this.user.postalCode;
    }
    if (this.user.password !== null) {
      user.password = this.user.password === '' ? null : this.user.password;
    }
    if (this.user.active !== null) {
      user.active = this.user.active;
    }
    user.googleAuthEnabled = this.user.googleAuthEnabled ? 1 : 0;
    this.userService.updateUserInfo(this.userId, user).subscribe(
      () => {
        this.Toast.fire({
          icon: 'success',
          title: 'اطلاعات کاربر با موفقیت بروز شد.'
        });
        this.userInfoLoading = false;
      },
      (response: HttpError) => {
        if (response.status !== 422) {
          this.Toast.fire({ icon: 'error', title: response.error });
        } else {
          this.error = response.validationError;
        }
        this.userInfoLoading = false;
      }
    );
  }

  updateUserVerified(): void {
    this.userVerifiedLoading = true;
    this.error = {};
    const user: any = {};
    user.addressVerified = this.user.addressVerified;
    user.bankVerified = this.user.bankVerified;
    user.identityCardVerified = this.user.identityCardVerified;
    user.identityVerified = this.user.identityVerified;
    user.phoneVerified = this.user.phoneVerified;
    user.emailVerified = this.user.emailVerified;
    user.landlinePhoneVerified = this.user.landlinePhoneVerified;
    this.userService.updateUserInfo(this.userId, user).subscribe(
      () => {
        this.Toast.fire({
          icon: 'success',
          title: 'اطلاعات کاربر با موفقیت بروز شد.'
        });
        this.userVerifiedLoading = false;
      },
      (response: HttpError) => {
        if (response.status !== 422) {
          this.Toast.fire({ icon: 'error', title: response.error });
        } else {
          this.error = response.validationError;
        }
        this.userVerifiedLoading = false;
      }
    );
  }

  getUserCreditCards(id: number): void {
    this.userService.getUserCreditCards(id).subscribe((cards) => {
      this.creditCards = cards;
    });
  }

  getUserWithdrawLimits(id: number): void {
    this.userService.getUserWithdrawLimits(id).subscribe((limits) => {
      this.userWithdrawLimits = limits.filter(i => i.monthly_withdraw_price);
      this.isSettingLimit = false;
      this.noLimit = '';
    }, (err) => {
      this.isSettingLimit = false;
      this.userWithdrawLimits = [];
      this.noLimit = err.error;
    });
  }

  setLimit(remove: UserLimit | null = null) {
    this.limit.user_id = this.userId.toString();
    if (remove?.currency) {
      this.limit.currency_id = remove.currency;
      this.limit.daily_withdraw_price = null;
      this.limit.monthly_withdraw_price = null;
      this.userWithdrawLimits.find(i => i.currency === remove.currency)!.loading = true;
    } else {
      this.isSettingLimit = true;
    }
    if (this.limit.currency_id === 'IRR') {
      if (this.limit.daily_withdraw_price) {
        this.limit.daily_withdraw_price = Big(this.limit.daily_withdraw_price).times(10).round(0, Big.roundDown).toNumber();
      }
      if (this.limit.monthly_withdraw_price) {
        this.limit.monthly_withdraw_price = Big(this.limit.monthly_withdraw_price).times(10).round(0, Big.roundDown).toNumber();
      }
    }
    this.userService.putUserWithdrawLimits(this.limit).subscribe(() => {
      if (remove) {
        this.userWithdrawLimits.find(i => i.currency === remove.currency)!.loading = true;
      }
      const limitId = this.limit.currency_id;
      this.limit = new UserLimit();
      this.limit.currency_id = limitId;
      this.getUserWithdrawLimits(this.userId);
    }, (err) => {
      this.isSettingLimit = false;
      console.error(err);
    });
  }

  getUserIbanList(id: number): void {
    this.userService.getUserIbanList(id).subscribe((ibans) => {
      this.userIban = ibans;
    });
  }

  setIbanStatus(iban: Iban, status: 'ACCEPTED' | 'REJECTED'): void {
    this.ibanLoading = true;
    this.selectedIban = iban;
    this.userService.setIbanStatus(iban.id, status).subscribe(
      () => {
        this.Toast.fire({
          icon: 'success',
          title: 'اطلاعات کاربر با موفقیت بروز شد.'
        });
        iban.verified = status;
        this.ibanLoading = false;
      },
      (response: HttpError) => {
        if (response.status !== 422) {
          this.Toast.fire({ icon: 'error', title: response.error });
        } else {
          this.error = response.validationError;
        }
        this.ibanLoading = false;
      }
    );
  }

  setCreditCardStatus(card: Card, status: 'ACCEPTED' | 'REJECTED'): void {
    this.selectedCard = card;
    this.creditCardLoading = true;
    this.userService.setCreditCardStatus(card.id, status).subscribe(
      () => {
        this.Toast.fire({
          icon: 'success',
          title: 'اطلاعات کاربر با موفقیت بروز شد.'
        });
        card.verified = status;
        this.creditCardLoading = false;
      },
      (response: HttpError) => {
        if (response.status !== 422) {
          this.Toast.fire({ icon: 'error', title: response.error });
        } else {
          this.error = response.validationError;
        }
        this.creditCardLoading = false;
      }
    );
  }

  deleteCreditCard(card: Card): void {
    Swal.fire({
      title: 'آیا مطمئن هستید؟',
      text: 'این عملیات قابل بازگشت نیست.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'بله',
      cancelButtonText: 'انصراف',
      confirmButtonColor: '#00d68f',
      cancelButtonColor: '#8f9bb3'
    }).then(
      (result) => {
        if (result.isConfirmed) {
          this.deleteLoading = true;
          this.selectedCard = card;
          this.userService.deleteCreditCard(card.id).subscribe(() => {
            this.Toast.fire({
              icon: 'success',
              title: 'کارت بانکی با موفقیت حذف شد.'
            });
            this.creditCards.splice(this.creditCards.indexOf(card), 1);
            this.deleteLoading = false;
          });
        }
      },
      (response: HttpError) => {
        if (response.status === 400) {
          this.Toast.fire({ icon: 'error', text: response.error });
        }
        this.deleteLoading = false;
      }
    );
  }

  getUserToken(): void {
    this.getTokenLoading = true;
    this.sub.sink = this.userService.getImpersonateToken(this.userId).subscribe(
      (response: any) => {
        const userToken = response.data?.token;
        this.openImpersonateModal(userToken);
        this.getTokenLoading = false;
      },
      (response: HttpError) => {
        if (response.status !== 422) {
          this.Toast.fire({ icon: 'error', text: response.error });
        }
      }
    );
  }

  checkScope(type: 'AND' | 'OR', ...scopes: string[]): boolean {
    return this.authenticationService.checkScope('AND', ...scopes);
  }

  deleteIban(iban: Iban): void {
    Swal.fire({
      title: 'آیا مطمئن هستید؟',
      text: 'این عملیات قابل بازگشت نیست.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'بله',
      cancelButtonText: 'انصراف',
      confirmButtonColor: '#00d68f',
      cancelButtonColor: '#8f9bb3'
    }).then(
      (result) => {
        if (result.isConfirmed) {
          this.deleteLoading = true;
          this.selectedIban = iban;
          this.userService.deleteIban(iban.id).subscribe(() => {
            this.Toast.fire({
              icon: 'success',
              title: 'کارت بانکی با موفقیت حذف شد.'
            });
            this.userIban.splice(this.userIban.indexOf(iban), 1);
            this.deleteLoading = false;
          });
        }
      },
      (response: HttpError) => {
        if (response.status === 400) {
          this.Toast.fire({ icon: 'error', title: response.error });
        }
        this.deleteLoading = false;
      }
    );
  }


  getFeeHistory(
    date: string[] = [this.dateService.previousWeek(), this.dateService.todayStringGregorian()]
  ): void {
    this.error = {};
    this.filterLoading = true;
  }

  submitFee(): void {
    this.feeLoading = true;
    const fee: any = {
      feePercent: null
    };
    this.error = {};
    if (
      !this.isDefault &&
      this.user.feePercent &&
      this.user.feePercent !== this.user.defaultTransactionFee
    ) {
      fee.feePercent = this.user.feePercent;
    } else {
      this.isDefault = true;
      this.user.feePercent = this.user.defaultTransactionFee;
    }
    this.userService.updateUserInfo(this.userId, fee).subscribe(
      (response) => {
        this.Toast.fire({
          icon: 'success',
          title: 'کارمزد با موفقیت ویرایش شد.'
        });
        this.feeLoading = false;
      },
      (error: HttpError) => {
        if (error.status !== 422) {
          this.Toast.fire({ icon: 'error', title: error.error });
        } else {
          this.error = error.validationError;
        }
        this.feeLoading = false;
      }
    );
  }

  getWallets(): void {
    this.userService.getUserWallets([this.userId]).subscribe((userAssets) => {
      this.userAsset = userAssets[0];
    });
  }


  tabChanged($event: NbTabComponent): void {
    this.tab = $event;
    if (this.tab.tabId === 'userImages' && this.viewers.length === 0) {
      setTimeout(() => {
        this.initImageViewer('identityImage');
        this.initImageViewer('nationalCardImage');
        this.initImageViewer('homeBill');
      });
    }
  }

  ngOnInit(): void {
    this.labels = this.userService.getLabelList();
    this.activatedRoute.params.subscribe((params) => {
      if (params.id) {
        this.userId = params.id;
        this.getUser();
        this.getUserLabels();
        this.getUserCreditCards(this.userId);
        this.getUserWithdrawLimits(this.userId);
        this.getUserIbanList(this.userId);
        this.getWallets();
        this.sub.sink = this.smartTableService.loadingEvent.subscribe(() => {
          this.tableLoading = true;
        });
      }
    });
    this.getFeeHistory();
  }

  changeUserLabel(labelId: number): void {
  }

  deleteUserLabel(label: Label): void {
    this.userLabels.splice(this.userLabels.indexOf(label), 1);
    this.userService
      .changeUserLabel(
        this.userLabels.map((_label) => _label.id),
        this.userId
      )
      .subscribe(() => {
        this.Toast.fire({
          icon: 'success',
          text: 'برچسب کاربر با موفقیت حذف شد.'
        });
      });
  }

  addLabel(label: Label): void {
    if (!this.userLabels.find((userLabel) => userLabel.id === label.id)) {
      this.userLabels.push(label);
      this.userService
        .changeUserLabel(
          this.userLabels.map((_label) => _label.id),
          this.userId
        )
        .subscribe(() => {
          this.Toast.fire({
            icon: 'success',
            text: 'برچسب کاربر با موفقیت تغییر کرد.'
          });
        });
    } else {
      this.Toast.fire({
        icon: 'error',
        text: 'کاربر در حال حاضر این برچسب را دارد'
      });
    }
  }

  openNotesModal(): void {
    this.dialogService.open(NoteComponent, {
      hasBackdrop: true,
      dialogClass: 'note-modal-container',
      context: {
        userId: this.userId
      }
    });
  }

  openImpersonateModal(token: string) {
    this.dialogService.open(ImpersonateTutorialComponent, {
      hasBackdrop: true,
      autoFocus:true,
      context: {
        impersonateCode: `localStorage.setItem('token','${token}')`
      }
    });
  }

  ngOnDestroy(): void {
    if (this.router.routerState.snapshot.url === '/dashboard/user') {
      this.userService.setUserListPage.next(this.userService.currentUserPage);
    }
    this.userService.currentUserPage = 1;
    this.sub.unsubscribe();
    this.viewers.map((viewer) => viewer?.destroy());
  }

}
